<!-- FILENAME: ResumeLogoEditor.vue -->
<template>
  <div class="logo-editor-container">
    <div class="editor-header">
      <h2>Resume Overlay Editor</h2>
      <p class="subtitle">Add logo, text, headers/footers, and redaction boxes</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading PDF preview...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-message">
      <p>❌ {{ error }}</p>
      <button @click="retryLoad">Retry</button>
    </div>

    <!-- PDF Editor Canvas -->
    <div v-if="!loading && !error" class="editor-workspace">
      <div class="canvas-container" ref="canvasContainer">
        <!-- PDF Canvas -->
        <canvas ref="pdfCanvas"></canvas>
        
         <!-- Interactive Blocks (when in redaction mode) -->
        
<div 
v-if="redactionMode"
v-for="(block, idx) in blocks.filter(b => !b.redacted)"
:key="`block-${idx}`"
class="block-overlay"
:class="{
  'text-block-overlay': block.type === 'text',
  'image-block-overlay': block.type === 'image'
}"
:style="{
  left: `${block.x * scale}px`,
  top: `${block.y * scale}px`,
  width: `${block.width * scale}px`,
  height: `${block.height * scale}px`
}"
@click="redactBlock(block)"
:title="block.type === 'text' 
  ? `Click to redact text: ${block.text}` 
  : 'Click to redact image'"
>
<div class="block-type-badge">
  <i :class="block.type === 'text' ? 'fas fa-font' : 'fas fa-image'"></i>
</div>
</div>

<!-- NEW: Show redacted blocks overlay ALWAYS (even when redaction mode off) -->
<div 
  v-for="(block, idx) in redactedBlocks"
  :key="`redacted-${idx}`"
  class="redacted-block-indicator"
  :style="{
    left: `${block.x * scale}px`,
    top: `${block.y * scale}px`,
    width: `${block.width * scale}px`,
    height: `${block.height * scale}px`
  }"
  @click="unredactBlock(block)"
  title="Click to remove redaction"
>
  <div class="redacted-badge">
    <i class="fas fa-times"></i>
  </div>
</div>
      

        <!-- Draggable Overlays with Resize Handles -->
        <div 
        v-for="overlay in overlays"
        :key="overlay.id"
        class="overlay-draggable"
        :class="[`overlay-${overlay.type}`, { selected: selectedOverlay === overlay.id }]"
        :style="getOverlayStyle(overlay)"
        @mousedown="startDrag($event, overlay)"
        @touchstart="startDrag($event, overlay)"
      >
          <!-- Logo -->
          <img 
            v-if="overlay.type === 'logo'" 
            :src="logoPreviewUrl" 
            alt="Logo" 
            style="width: 100%; height: 100%; object-fit: contain; pointer-events: none;"
          />
          
          <!-- Text -->
          <div 
          v-if="overlay.type === 'text'" 
          class="text-overlay"
          :style="{ fontSize: overlay.fontSize + 'px', color: overlay.color }"
        >
          {{ overlay.text }}
        </div>
        
        <!-- Rectangle -->
        <div 
          v-if="overlay.type === 'rectangle'" 
          class="rectangle-overlay"
          :style="{ backgroundColor: overlay.color, opacity: overlay.opacity }"
        >
        </div>
        
        <!-- Delete button -->
        <button class="delete-overlay-btn" @click.stop="deleteOverlay(overlay.id)">×</button>
          <!-- Resize Handles (only show when selected) -->
          <template v-if="selectedOverlay === overlay.id && !dragging">
            <div class="resize-handle nw" @mousedown.stop="startResize($event, overlay, 'nw')"></div>
            <div class="resize-handle n" @mousedown.stop="startResize($event, overlay, 'n')"></div>
            <div class="resize-handle ne" @mousedown.stop="startResize($event, overlay, 'ne')"></div>
            <div class="resize-handle e" @mousedown.stop="startResize($event, overlay, 'e')"></div>
            <div class="resize-handle se" @mousedown.stop="startResize($event, overlay, 'se')"></div>
            <div class="resize-handle s" @mousedown.stop="startResize($event, overlay, 's')"></div>
            <div class="resize-handle sw" @mousedown.stop="startResize($event, overlay, 'sw')"></div>
            <div class="resize-handle w" @mousedown.stop="startResize($event, overlay, 'w')"></div>
          </template>

        </div>
      </div>

      <!-- Controls -->
      <div class="controls-panel">
        <!-- Redaction Mode Toggle -->
        <div class="redaction-mode-section">
          <h3>Interactive Redaction</h3>
          <button 
            class="add-btn"
            :class="{ 'redaction-mode-active': redactionMode }"
            @click="toggleRedactionMode"
          >
            <i class="fas fa-crosshairs"></i> 
            {{ redactionMode ? 'Exit Click-to-Redact Mode' : 'Enable Click-to-Redact Mode' }}
          </button>
          <p v-if="redactionMode" class="redaction-hint">
            💡 Click on any text or image in the PDF to redact it
          </p>
          <div v-if="redactionMode && blockStats" class="block-stats">
            <span class="stat-badge">
              <i class="fas fa-font"></i> {{ blockStats.textCount }} text blocks
            </span>
            <span class="stat-badge">
              <i class="fas fa-image"></i> {{ blockStats.imageCount }} images
            </span>
          </div>
        </div>
        <!-- Add Overlay Section -->
        <div class="add-overlay-section">
          <h3>Add Overlay</h3>
          
          <button class="add-btn add-logo-btn" @click="addOverlay('logo')">
            <i class="fas fa-image"></i> Add Logo
          </button>
          
          <button class="add-btn add-text-btn" @click="showAddTextModal = true">
            <i class="fas fa-font"></i> Add Text
          </button>
          
          <button class="add-btn add-rectangle-btn" @click="showAddRectModal = true">
            <i class="fas fa-square"></i> Add Redaction Box
          </button>
          
          <button class="add-btn add-candidate-id-btn" @click="addCandidateIdText">
            <i class="fas fa-id-card"></i> Add Candidate ID
          </button>
        </div>

        <!-- Quick Templates -->
        <div class="templates-section">
          <h3>Quick Templates</h3>
          <button class="template-btn" @click="addHeaderFooter">
            <i class="fas fa-bars"></i> Add Red Header & Footer
          </button>
        </div>

        <!-- Current Overlays List -->
        <div class="overlays-list-section">
          <h3>Current Overlays ({{ overlays.length }})</h3>
          <div v-if="overlays.length === 0" class="no-overlays">
            No overlays added yet
          </div>
          <div v-else class="overlays-list">
            <div 
              v-for="overlay in overlays" 
              :key="overlay.id" 
              class="overlay-item"
              :class="{ selected: selectedOverlay === overlay.id }"
              @click="selectedOverlay = overlay.id"
            >
              <i :class="getOverlayIcon(overlay.type)"></i>
              <span>{{ getOverlayLabel(overlay) }}</span>
              <button @click.stop="deleteOverlay(overlay.id)" class="delete-btn-mini">×</button>
            </div>
          </div>
        </div>

        <!-- Export Options -->
        <div class="export-section">
          <label>
            <input type="checkbox" v-model="applyToAllPages" />
            Apply to all pages
          </label>
          
        <!-- NEW: Redaction mode toggle -->
        <label>
          <input type="checkbox" v-model="useTrueRedaction" />
          <strong>True Redaction</strong> (permanently remove text under rectangles)
        </label>
          <button 
            class="btn-primary" 
            @click="exportWithOverlays" 
            :disabled="exporting || overlays.length === 0"
          >
            {{ exporting ? 'Exporting...' : 'Export PDF with Overlays' }}
          </button>
          
          <button class="btn-secondary" @click="clearAllOverlays">
            Clear All
          </button>
          
          <button class="btn-cancel" @click="$emit('close')">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Add Text Modal -->
    <div v-if="showAddTextModal" class="modal-overlay" @click="showAddTextModal = false">
      <div class="modal-box" @click.stop>
        <h3>Add Text Overlay</h3>
        <label>
          Text:
          <input v-model="newText.text" type="text" placeholder="Enter text..." />
        </label>
        <label>
          Font Size:
          <input v-model.number="newText.fontSize" type="number" min="8" max="72" />
        </label>
        <label>
          Color:
          <input v-model="newText.color" type="color" />
        </label>
        <div class="modal-actions">
          <button @click="addTextOverlay" class="btn-primary">Add</button>
          <button @click="showAddTextModal = false" class="btn-cancel">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Add Rectangle Modal -->
    <div v-if="showAddRectModal" class="modal-overlay" @click="showAddRectModal = false">
      <div class="modal-box" @click.stop>
        <h3>Add Redaction Box</h3>
        <label>
          Width (px):
          <input v-model.number="newRect.width" type="number" min="20" max="600" />
        </label>
        <label>
          Height (px):
          <input v-model.number="newRect.height" type="number" min="20" max="400" />
        </label>
        <label>
          Color:
          <input v-model="newRect.color" type="color" />
        </label>
        <label>
          Opacity:
          <input v-model.number="newRect.opacity" type="range" min="0" max="1" step="0.1" />
          {{ Math.round(newRect.opacity * 100) }}%
        </label>
        <div class="modal-actions">
          <button @click="addRectangleOverlay" class="btn-primary">Add</button>
          <button @click="showAddRectModal = false" class="btn-cancel">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';

export default {
  name: 'ResumeLogoEditor',
  
  props: {
    candidateId: { type: String, required: true },
    idType: { type: String, required: true },
    pdfUrl: { type: String, required: true }
  },

  data() {
    return {
      loading: true,
      redactedBlocks: [],  // NEW: track blocks marked for redaction

 // Interactive redaction
      redactionMode: false,
      blocks: [],  // Changed from textBlocks to handle both text and images
      blockStats: null,
      useTrueRedaction: false,
      
      error: null,
      exporting: false,
      
      // PDF data
      pdfDoc: null,
      loadingTask: null,
      canvasWidth: 0,
      canvasHeight: 0,
      scale: 1,
      dpr: window.devicePixelRatio || 1,
      pdfPageWidth: 0,
      pdfPageHeight: 0,
      
      // Overlays (replaces single logo)
      overlays: [],
      nextOverlayId: 1,
      selectedOverlay: null,
      
      // Logo settings
      logoPreviewUrl: '/red-tic.png',
      defaultLogoSize: 120,
      
      // Drag state
      dragging: false,
      draggedOverlay: null,
      dragOffset: { x: 0, y: 0 },
      
      // Resize state
      resizing: false,
      resizedOverlay: null,
      resizeHandle: null,
      resizeStartPos: { x: 0, y: 0 },
      resizeStartBounds: { x: 0, y: 0, width: 0, height: 0 },
      
      // Options
      applyToAllPages: false,
      
      // Modals
      showAddTextModal: false,
      showAddRectModal: false,
      
      // New overlay forms
      newText: {
        text: '',
        fontSize: 16,
        color: '#B21C28'
      },
      newRect: {
        width: 200,
        height: 50,
        color: '#FF0000',
        opacity: 1
      }
    };
  },

  mounted() {
    this.loadPdfJs().then(() => {
      this.initializeEditor();
      this.setupDragListeners();
    });
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {

unredactBlock(block) {
  console.log('↩️ Removing redaction');
  
  // Remove from redacted list
  this.redactedBlocks = this.redactedBlocks.filter(b => b !== block);
  
  // Restore to blocks list if in redaction mode
  if (this.redactionMode) {
    block.redacted = false;
    // Update stats
    if (block.type === 'text') {
      this.blockStats.textCount++;
    } else {
      this.blockStats.imageCount++;
    }
  }
},
    //redaction 
    async toggleRedactionMode() {
  this.redactionMode = !this.redactionMode;
  
  if (this.redactionMode) {
    console.log('🎯 Entering redaction mode...');
    await this.loadBlocks();
  } else {
    console.log('❌ Exiting redaction mode');
    // DON'T clear blocks - just hide the UI
    // Keep redactedBlocks intact so they export
    console.log(`✅ Keeping ${this.redactedBlocks.length} redacted blocks for export`);
  }
},

    async loadBlocks() {
      try {
        const apiBaseUrl = this.getApiBaseUrl();
        const url = `${apiBaseUrl}/api/v1/resume/${this.candidateId}/text-blocks?id_type=${this.idType}`;
        
        console.log('📍 Loading text and image blocks...');
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Failed to load blocks');
        }
        
        const data = await response.json();
        this.blocks = data.blocks;
        this.blockStats = {
          textCount: data.text_count,
          imageCount: data.image_count
        };
        
        console.log(`✅ Loaded ${data.text_count} text blocks and ${data.image_count} images`);
      } catch (err) {
        console.error('❌ Failed to load blocks:', err);
        alert('Failed to load blocks for redaction');
        this.redactionMode = false;
      }
    },

    redactBlock(block) {
      const blockLabel = block.type === 'text' 
        ? `"${block.text}"` 
        : 'image';
      
      console.log(`🔒 Redacting ${block.type}:`, blockLabel);
      block.redacted = true; // Mark it as redacted
      // Store in separate redaction list for export
      if (!this.redactedBlocks) {
          this.redactedBlocks = [];
        }
        this.redactedBlocks.push(block);
        
        // Update stats
        if (block.type === 'text') {
          this.blockStats.textCount--;
        } else {
          this.blockStats.imageCount--;
        }
      // Visual feedback
      const notification = document.createElement('div');
      notification.className = 'redaction-notification';
      notification.innerHTML = `
        <i class="fas fa-check-circle"></i>
        Marked for redaction: ${blockLabel}
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.remove();
      }, 2000);
    },
    // === PDF Loading ===
    loadPdfJs() {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          resolve();
        };
        script.onerror = () => reject(new Error('Failed to load PDF.js'));
        document.head.appendChild(script);
      });
    },

    async initializeEditor() {
      try {
        this.loading = true;
        this.error = null;

        const pdfjsLib = window.pdfjsLib;
        const loadingTask = pdfjsLib.getDocument({ url: this.pdfUrl, withCredentials: false });
        this.loadingTask = markRaw(loadingTask);
        
        const pdfDoc = await loadingTask.promise;
        this.pdfDoc = markRaw(pdfDoc);
        
        this.loading = false;
        await this.$nextTick();
        await this.renderPage();

        console.log('✅ PDF editor initialized');
      } catch (err) {
        console.error('❌ PDF loading failed:', err);
        this.error = err.message || 'Failed to load PDF';
        this.loading = false;
      }
    },

    async renderPage() {
      try {
        const page = await this.pdfDoc.getPage(1);
        const actualViewport = page.getViewport({ scale: 1 });
        this.pdfPageWidth = actualViewport.width;
        this.pdfPageHeight = actualViewport.height;
        
        const maxWidth = window.innerWidth * 0.5;
        const maxHeight = window.innerHeight * 0.8;
        const scaleX = maxWidth / actualViewport.width;
        const scaleY = maxHeight / actualViewport.height;
        this.scale = Math.min(scaleX, scaleY, 1.5);
        
        const viewport = page.getViewport({ scale: this.scale });
        const canvas = this.$refs.pdfCanvas;
        const context = canvas.getContext('2d');

        this.canvasWidth = viewport.width;
        this.canvasHeight = viewport.height;
        canvas.style.width = `${this.canvasWidth}px`;
        canvas.style.height = `${this.canvasHeight}px`;
        canvas.width = Math.floor(this.canvasWidth * this.dpr);
        canvas.height = Math.floor(this.canvasHeight * this.dpr);
        context.scale(this.dpr, this.dpr);

        await page.render({ canvasContext: context, viewport: viewport }).promise;
        console.log('✅ PDF rendered:', this.canvasWidth, 'x', this.canvasHeight);
      } catch (err) {
        console.error('❌ Page rendering failed:', err);
        throw err;
      }
    },

    // === Overlay Management ===
    addOverlay(type, config = {}) {
      const overlay = {
        id: this.nextOverlayId++,
        type,
        x: 50,
        y: 50,
        ...config
      };

      if (type === 'logo') {
        overlay.width = this.defaultLogoSize;
        overlay.height = this.defaultLogoSize / 2;
      }

      this.overlays.push(overlay);
      this.selectedOverlay = overlay.id;
      console.log('✅ Added overlay:', overlay);
    },

    addTextOverlay() {
      if (!this.newText.text.trim()) {
        alert('Please enter text');
        return;
      }
      
      this.addOverlay('text', {
        text: this.newText.text,
        fontSize: this.newText.fontSize,
        color: this.newText.color,
        width: 200,
        height: 30
      });
      
      this.newText.text = '';
      this.showAddTextModal = false;
    },

    addRectangleOverlay() {
      this.addOverlay('rectangle', {
        width: this.newRect.width,
        height: this.newRect.height,
        color: this.newRect.color,
        opacity: this.newRect.opacity
      });
      this.showAddRectModal = false;
    },

    addCandidateIdText() {
      this.addOverlay('text', {
        text: `ID: ${this.candidateId}`,
        fontSize: 14,
        color: '#333333',
        width: 150,
        height: 25
      });
    },

    addHeaderFooter() {
      // Red header at top
      this.addOverlay('rectangle', {
        x: 0,
        y: 0,
        width: this.canvasWidth,
        height: 40,
        color: '#B21C28',
        opacity: 1
      });
      
      // Red footer at bottom
      this.addOverlay('rectangle', {
        x: 0,
        y: this.canvasHeight - 40,
        width: this.canvasWidth,
        height: 40,
        color: '#B21C28',
        opacity: 0.9
      });
    },

    deleteOverlay(id) {
      this.overlays = this.overlays.filter(o => o.id !== id);
      if (this.selectedOverlay === id) {
        this.selectedOverlay = null;
      }
    },

    clearAllOverlays() {
      if (confirm('Clear all overlays?')) {
        this.overlays = [];
        this.selectedOverlay = null;
      }
    },

    getOverlayStyle(overlay) {
      return {
        left: `${overlay.x}px`,
        top: `${overlay.y}px`,
        width: `${overlay.width}px`,
        height: `${overlay.height}px`,
        cursor: this.dragging ? 'grabbing' : (this.resizing ? 'nwse-resize' : 'grab')
      };
    },

    getOverlayIcon(type) {
      const icons = {
        logo: 'fas fa-image',
        text: 'fas fa-font',
        rectangle: 'fas fa-square'
      };
      return icons[type] || 'fas fa-layer-group';
    },

    getOverlayLabel(overlay) {
      if (overlay.type === 'text') return overlay.text;
      if (overlay.type === 'logo') return 'Logo';
      if (overlay.type === 'rectangle') return `Box (${overlay.width}x${overlay.height})`;
      return overlay.type;
    },

    // === Drag Handlers ===
    setupDragListeners() {
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('touchmove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchend', this.stopDrag);

       // Resize listeners
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
    },

    startDrag(event, overlay) {
      event.preventDefault();
      this.dragging = true;
      this.draggedOverlay = overlay;
      this.selectedOverlay = overlay.id;

      const containerRect = this.$refs.canvasContainer.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      this.dragOffset.x = clientX - containerRect.left - overlay.x;
      this.dragOffset.y = clientY - containerRect.top - overlay.y;
    },

    onDrag(event) {
      if (!this.dragging || !this.draggedOverlay) return;
      event.preventDefault();

      const containerRect = this.$refs.canvasContainer.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      let newX = clientX - containerRect.left - this.dragOffset.x;
      let newY = clientY - containerRect.top - this.dragOffset.y;

      // Clamp to canvas bounds
      newX = Math.max(0, Math.min(newX, this.canvasWidth - this.draggedOverlay.width));
      newY = Math.max(0, Math.min(newY, this.canvasHeight - this.draggedOverlay.height));

      this.draggedOverlay.x = newX;
      this.draggedOverlay.y = newY;
    },

    stopDrag() {
      this.dragging = false;
      this.draggedOverlay = null;
    },


    // === Resize Handlers (NEW) ===
    startResize(event, overlay, handle) {
      event.preventDefault();
      event.stopPropagation();
      
      this.resizing = true;
      this.resizedOverlay = overlay;
      this.resizeHandle = handle;
      this.selectedOverlay = overlay.id;

      const containerRect = this.$refs.canvasContainer.getBoundingClientRect();
      this.resizeStartPos = {
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top
      };
      
      this.resizeStartBounds = {
        x: overlay.x,
        y: overlay.y,
        width: overlay.width,
        height: overlay.height
      };

      console.log('🔧 Start resize:', handle);
    },

    onResize(event) {
      if (!this.resizing || !this.resizedOverlay) return;
      event.preventDefault();

      const containerRect = this.$refs.canvasContainer.getBoundingClientRect();
      const currentX = event.clientX - containerRect.left;
      const currentY = event.clientY - containerRect.top;
      
      const deltaX = currentX - this.resizeStartPos.x;
      const deltaY = currentY - this.resizeStartPos.y;

      const overlay = this.resizedOverlay;
      const start = this.resizeStartBounds;
      const minSize = 20;

      // Apply resize based on handle
      switch (this.resizeHandle) {
        case 'se': // Bottom-right
          overlay.width = Math.max(minSize, start.width + deltaX);
          overlay.height = Math.max(minSize, start.height + deltaY);
          break;
        
        case 'nw': // Top-left
          const newWidth = Math.max(minSize, start.width - deltaX);
          const newHeight = Math.max(minSize, start.height - deltaY);
          overlay.x = start.x + (start.width - newWidth);
          overlay.y = start.y + (start.height - newHeight);
          overlay.width = newWidth;
          overlay.height = newHeight;
          break;
        
        case 'ne': // Top-right
          overlay.width = Math.max(minSize, start.width + deltaX);
          const newHeightNE = Math.max(minSize, start.height - deltaY);
          overlay.y = start.y + (start.height - newHeightNE);
          overlay.height = newHeightNE;
          break;
        
        case 'sw': // Bottom-left
          const newWidthSW = Math.max(minSize, start.width - deltaX);
          overlay.x = start.x + (start.width - newWidthSW);
          overlay.width = newWidthSW;
          overlay.height = Math.max(minSize, start.height + deltaY);
          break;
        
        case 'n': // Top
          const newHeightN = Math.max(minSize, start.height - deltaY);
          overlay.y = start.y + (start.height - newHeightN);
          overlay.height = newHeightN;
          break;
        
        case 's': // Bottom
          overlay.height = Math.max(minSize, start.height + deltaY);
          break;
        
        case 'e': // Right
          overlay.width = Math.max(minSize, start.width + deltaX);
          break;
        
        case 'w': // Left
          const newWidthW = Math.max(minSize, start.width - deltaX);
          overlay.x = start.x + (start.width - newWidthW);
          overlay.width = newWidthW;
          break;
      }     // Clamp to canvas bounds
      overlay.x = Math.max(0, Math.min(overlay.x, this.canvasWidth - overlay.width));
      overlay.y = Math.max(0, Math.min(overlay.y, this.canvasHeight - overlay.height));
      overlay.width = Math.min(overlay.width, this.canvasWidth - overlay.x);
      overlay.height = Math.min(overlay.height, this.canvasHeight - overlay.y);
    },

     stopResize() {
      if (this.resizing) {
        console.log('🔧 Stop resize');
      }
      this.resizing = false;
      this.resizedOverlay = null;
      this.resizeHandle = null;
    },
    // === Export ===
    async exportWithOverlays() {
  this.exporting = true;

  try {
    // Convert regular overlays (logo, text, non-redacted rectangles)
    // const pdfOverlays = this.overlays.map(overlay => {
    //   const pdfOverlay = {
    //     type: overlay.type,
    //     x: Math.round(overlay.x / this.scale),
    //     y: Math.round(overlay.y / this.scale),
    //     width: Math.round(overlay.width / this.scale),
    //     height: Math.round(overlay.height / this.scale)
    //   };

    //   if (overlay.type === 'text') {
    //     pdfOverlay.text = overlay.text;
    //     pdfOverlay.fontSize = Math.round(overlay.fontSize / this.scale);
    //     pdfOverlay.color = overlay.color;
    //   }

    //   if (overlay.type === 'rectangle') {
    //     pdfOverlay.color = overlay.color;
    //     pdfOverlay.opacity = overlay.opacity;
    //   }

    //   return pdfOverlay;
    // });
    // REPLACE WITH:
const pdfOverlays = [];
const rectangleRedactions = [];

this.overlays.forEach(overlay => {
  if (overlay.type === 'rectangle') {
    // Rectangles become redactions (remove content)
    rectangleRedactions.push({
      x: Math.round(overlay.x / this.scale),
      y: Math.round(overlay.y / this.scale),
      width: Math.round(overlay.width / this.scale),
      height: Math.round(overlay.height / this.scale)
    });
  } else {
    // Logo and text remain as overlays
    const pdfOverlay = {
      type: overlay.type,
      x: Math.round(overlay.x / this.scale),
      y: Math.round(overlay.y / this.scale),
      width: Math.round(overlay.width / this.scale),
      height: Math.round(overlay.height / this.scale)
    };

    if (overlay.type === 'text') {
      pdfOverlay.text = overlay.text;
      pdfOverlay.fontSize = Math.round(overlay.fontSize / this.scale);
      pdfOverlay.color = overlay.color;
    }

    pdfOverlays.push(pdfOverlay);
  }
});

    // Convert redacted blocks to redaction rectangles
   // Convert click-to-redact blocks
const clickRedactions = (this.redactedBlocks || []).map(block => ({
  x: Math.round(block.x),
  y: Math.round(block.y),
  width: Math.round(block.width),
  height: Math.round(block.height)
}));

    const allRedactions = [...clickRedactions, ...rectangleRedactions];

    const requestBody = {
      overlays: pdfOverlays,
      redactions: allRedactions,  // NEW: send redaction areas separately
      all_pages: this.applyToAllPages
    };

    console.log('🚀 Export request:', requestBody);

    const apiBaseUrl = this.getApiBaseUrl();
    const url = `${apiBaseUrl}/api/v1/resume/${this.candidateId}/add-overlays?id_type=${this.idType}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `Resume_${this.candidateId}_with_overlays.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);

    console.log('✅ PDF exported');
    this.$emit('export-success');
    
    setTimeout(() => this.$emit('close'), 1000);

  } catch (err) {
    console.error('Export failed:', err);
    alert(`Export failed: ${err.message}`);
  } finally {
    this.exporting = false;
  }
},

    retryLoad() {
      this.error = null;
      this.initializeEditor();
    },

    cleanup() {
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchend', this.stopDrag);
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.stopResize);

      if (this.loadingTask) {
        this.loadingTask.destroy();
      }
      this.pdfDoc = null;
    },

    getApiBaseUrl() {
      if (process.env.NODE_ENV === 'development') {
        return process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000';
      }
      return '';
    }
  }
};
</script>

<style scoped>

/* Add these new styles for resize handles */
.overlay-draggable.selected {
  border-color: #2196F3;
  border-width: 2px;
  box-shadow: 0 0 0 1px #2196F3;
}

.resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border: 2px solid #2196F3;
  border-radius: 50%;
  z-index: 30;
}

/* Corner handles */
.resize-handle.nw {
  top: -5px;
  left: -5px;
  cursor: nwse-resize;
}

.resize-handle.ne {
  top: -5px;
  right: -5px;
  cursor: nesw-resize;
}

.resize-handle.se {
  bottom: -5px;
  right: -5px;
  cursor: nwse-resize;
}

.resize-handle.sw {
  bottom: -5px;
  left: -5px;
  cursor: nesw-resize;
}

/* Edge handles */
.resize-handle.n {
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
  cursor: ns-resize;
}

.resize-handle.s {
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  cursor: ns-resize;
}

.resize-handle.e {
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  cursor: ew-resize;
}

.resize-handle.w {
  left: -5px;
  top: 50%;
  transform: translateY(-50%);
  cursor: ew-resize;
}

.resize-handle:hover {
  background: #2196F3;
  transform: scale(1.2);
}

.logo-editor-container {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  max-width: 95vw;
  max-height: 95vh;
  margin: 0 auto;
  overflow: auto;
}

.editor-header {
  margin-bottom: 20px;
}

.editor-header h2 {
  margin: 0 0 8px 0;
  color: #333;
}

.subtitle {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #B21C28;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
}

.editor-workspace {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  max-height: 80vh;
}

.canvas-container {
  position: relative;
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  display: inline-block;
  flex-shrink: 0;
  max-height: 75vh;
  overflow: visible;
  isolation: isolate;
  transform: translateZ(0);
}

canvas {
  display: block;
  pointer-events: none;
  position: relative;
  z-index: 1;
}

.overlay-draggable {
  position: absolute;
  border: 2px dashed #4CAF50;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  transition: box-shadow 0.2s;
  z-index: 10;
  pointer-events: auto;
  transform: none;
  will-change: left, top;
}

.overlay-draggable:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  border-color: #45a049;
}

.overlay-logo {
  background: rgba(255, 255, 255, 0.9);
  padding: 4px;
}

.overlay-text {
  background: rgba(255, 255, 255, 0.95);
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.text-overlay {
  font-weight: 500;
  white-space: nowrap;
  user-select: none;
}

.overlay-rectangle {
  border: 2px dashed rgba(255, 255, 255, 0.5);
}

.rectangle-overlay {
  width: 100%;
  height: 100%;
}

.delete-overlay-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f44336;
  color: white;
  border: 2px solid white;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.overlay-draggable:hover .delete-overlay-btn {
  display: flex;
}

.controls-panel {
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  max-height: 75vh;
  overflow-y: auto;
}

.controls-panel h3 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.add-overlay-section,
.templates-section,
.overlays-list-section,
.export-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
  color: black;
}

.export-section {
  border-bottom: none;
}

.add-btn,
.template-btn {
  width: 100%;
  padding: 12px;
  margin-bottom: 8px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.add-logo-btn {
  background: #4CAF50;
  color: white;
}

.add-logo-btn:hover {
  background: #45a049;
}

.add-text-btn {
  background: #2196F3;
  color: white;
}

.add-text-btn:hover {
  background: #0b7dda;
}

.add-rectangle-btn {
  background: #f44336;
  color: white;
}

.add-rectangle-btn:hover {
  background: #da190b;
}

.add-candidate-id-btn {
  background: #FF9800;
  color: white;
}

.add-candidate-id-btn:hover {
  background: #e68900;
}

.template-btn {
  background: #9C27B0;
  color: white;
}

.template-btn:hover {
  background: #7b1fa2;
}

.no-overlays {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 14px;
}

.overlays-list {
  max-height: 200px;
  overflow-y: auto;
}

.overlay-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.overlay-item:hover {
  background: #f5f5f5;
}

.overlay-item.selected {
  background: #e3f2fd;
  border-left: 3px solid #2196F3;
}

.overlay-item i {
  width: 20px;
  color: #666;
}

.overlay-item span {
  flex: 1;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-btn-mini {
  background: #f44336;
  color: white;
  border: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.export-section label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  cursor: pointer;
}

.btn-primary,
.btn-secondary,
.btn-cancel {
  width: 100%;
  padding: 12px;
  margin-bottom: 8px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #B21C28;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #951721;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e8e8e8;
}

/* Modal Styles - UPDATED for transparent background */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent; /* Changed from rgba(0, 0, 0, 0.5) */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  pointer-events: none;
}

.modal-box {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  pointer-events: auto;
  border: 2px solid #ddd;
}

.modal-box h3 {
  margin: 0 0 20px 0;
  color: #333;
}

.modal-box label {
  display: block;
  margin-bottom: 16px;
  color: #333;
  font-weight: 500;
}

.modal-box input[type="text"],
.modal-box input[type="number"],
.modal-box input[type="color"],
.modal-box input[type="range"] {
  width: 100%;
  padding: 8px;
  margin-top: 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  gap: 8px;
  margin-top: 20px;
}

.modal-actions button {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

@media (max-width: 900px) {
  .editor-workspace {
    flex-direction: column;
  }
  
  .controls-panel {
    width: 100%;
    max-height: none;
  }
}


/* Block Overlay Styles */
.block-overlay {
  position: absolute;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 5;
  pointer-events: auto;
}

.text-block-overlay {
  border: 2px dashed #FF5722;
  background: rgba(255, 87, 34, 0.1);
}

.text-block-overlay:hover {
  background: rgba(255, 87, 34, 0.3);
  border-color: #E64A19;
  transform: scale(1.02);
}

.image-block-overlay {
  border: 2px dashed #2196F3;
  background: rgba(33, 150, 243, 0.1);
}

.image-block-overlay:hover {
  background: rgba(33, 150, 243, 0.3);
  border-color: #1976D2;
  transform: scale(1.02);
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.2);
}

.block-type-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.text-block-overlay .block-type-badge {
  color: #FF5722;
  border: 2px solid #FF5722;
}

.image-block-overlay .block-type-badge {
  color: #2196F3;
  border: 2px solid #2196F3;
}

.block-stats {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
}

.stat-badge i {
  font-size: 11px;
}

.redaction-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #4CAF50;
  color: white;
  padding: 12px 20px;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  z-index: 10000;
  animation: slideIn 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

@keyframes slideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.redaction-notification i {
  font-size: 16px;
}
.block-overlay.redacted {
  background: rgba(255, 0, 0, 0.6) !important;
  border-color: #D32F2F !important;
  border-style: solid !important;
  pointer-events: none;
}

.block-overlay.redacted .block-type-badge {
  background: #D32F2F !important;
  color: white !important;
  border-color: #D32F2F !important;
}


.redacted-block-indicator {
  position: absolute;
  background: rgba(255, 0, 0, 0.5);
  border: 2px solid #D32F2F;
  cursor: pointer;
  z-index: 15;
  pointer-events: auto;
  transition: all 0.2s;
}

.redacted-block-indicator:hover {
  background: rgba(255, 0, 0, 0.7);
  transform: scale(1.02);
}

.redacted-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #D32F2F;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
</style>